import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import {
  convertUTCTimeToLocal,
  formatDayOffset,
} from '@/sections/attacks/capabilities/utils/timezone';
import { cn } from '@/utils/classname';

export interface ScheduleTimeInputProps {
  time: string; // HH:MM format (UTC)
  onTimeChange: (time: string) => void;
  selectedDays: string[]; // ['mon', 'wed', 'fri']
  perDayTimes?: Record<string, string>; // { mon: '09:00', wed: '10:00' }
  onPerDayTimesChange?: (times: Record<string, string>) => void;
  disabled?: boolean;
}

interface DayConfig {
  key: string;
  shortLabel: string;
  fullLabel: string;
}

const DAYS: DayConfig[] = [
  { key: 'mon', shortLabel: 'Mon', fullLabel: 'Monday' },
  { key: 'tue', shortLabel: 'Tue', fullLabel: 'Tuesday' },
  { key: 'wed', shortLabel: 'Wed', fullLabel: 'Wednesday' },
  { key: 'thu', shortLabel: 'Thu', fullLabel: 'Thursday' },
  { key: 'fri', shortLabel: 'Fri', fullLabel: 'Friday' },
  { key: 'sat', shortLabel: 'Sat', fullLabel: 'Saturday' },
  { key: 'sun', shortLabel: 'Sun', fullLabel: 'Sunday' },
];

/**
 * Format local time display string
 */
function formatLocalTimeDisplay(utcTime: string): string {
  if (!utcTime) return '';

  const local = convertUTCTimeToLocal(utcTime);
  if (!local.time) return '';

  const dayOffsetText = formatDayOffset(local.dayOffset);
  return `${local.time} ${local.timezone}${dayOffsetText ? ` (${dayOffsetText})` : ''}`;
}

/**
 * ScheduleTimeInput - Single or per-day time input with local time display
 *
 * Features:
 * - Single time input by default with UTC label
 * - Local time conversion displayed as helper text
 * - Expandable per-day time inputs for selected days
 * - Animated expansion/collapse
 *
 * @param time - Single time value in HH:MM format (UTC)
 * @param onTimeChange - Callback when single time changes
 * @param selectedDays - Array of selected day keys for per-day mode
 * @param perDayTimes - Optional per-day time values
 * @param onPerDayTimesChange - Callback when per-day times change
 * @param disabled - Disable all inputs
 */
export function ScheduleTimeInput({
  time,
  onTimeChange,
  selectedDays,
  perDayTimes,
  onPerDayTimesChange,
  disabled = false,
}: ScheduleTimeInputProps) {
  const [isPerDayMode, setIsPerDayMode] = useState(false);

  // Initialize per-day times from single time if not provided
  const effectivePerDayTimes: Record<string, string> =
    perDayTimes ??
    selectedDays.reduce<Record<string, string>>(
      (acc, day) => ({ ...acc, [day]: time }),
      {}
    );

  const handleToggleMode = () => {
    if (disabled) return;
    setIsPerDayMode(!isPerDayMode);
  };

  const handlePerDayTimeChange = (dayKey: string, newTime: string) => {
    if (onPerDayTimesChange) {
      onPerDayTimesChange({
        ...effectivePerDayTimes,
        [dayKey]: newTime,
      });
    }
  };

  const localTimeDisplay = formatLocalTimeDisplay(time);

  return (
    <div className="space-y-3">
      <AnimatePresence mode="wait">
        {!isPerDayMode ? (
          <motion.div
            key="single-time"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="space-y-2">
              <label
                htmlFor="schedule-time"
                className="text-chariot-text-secondary text-sm font-medium"
              >
                At time (UTC):
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="schedule-time"
                  type="text"
                  role="textbox"
                  aria-label="Schedule time in UTC"
                  value={time}
                  onChange={e => onTimeChange(e.target.value)}
                  disabled={disabled}
                  placeholder="HH:MM"
                  className={cn(
                    'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
                    'placeholder-chariot-text-tertiary w-24 rounded-md border px-3 py-2 text-sm',
                    'focus:border-chariot-brand-primary transition-colors focus:outline-hidden',
                    disabled && 'cursor-not-allowed opacity-50'
                  )}
                />
                {localTimeDisplay && (
                  <span className="text-chariot-text-tertiary text-sm">
                    ({localTimeDisplay} your local time)
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="per-day-times"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="space-y-3">
              <label className="text-chariot-text-secondary text-sm font-medium">
                Time per day (UTC):
              </label>
              <div className="space-y-2">
                {DAYS.filter(day => selectedDays.includes(day.key)).map(day => {
                  const dayTime = effectivePerDayTimes[day.key] || time;
                  const dayLocalTime = formatLocalTimeDisplay(dayTime);

                  return (
                    <div
                      key={day.key}
                      className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3"
                    >
                      <label
                        htmlFor={`time-${day.key}`}
                        className="text-chariot-text-primary w-24 text-sm font-medium"
                      >
                        {day.fullLabel}
                      </label>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <input
                          id={`time-${day.key}`}
                          type="text"
                          role="textbox"
                          aria-label={`${day.fullLabel} time in UTC`}
                          value={dayTime}
                          onChange={e =>
                            handlePerDayTimeChange(day.key, e.target.value)
                          }
                          disabled={disabled}
                          placeholder="HH:MM"
                          className={cn(
                            'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
                            'placeholder-chariot-text-tertiary w-24 rounded-md border px-3 py-2 text-sm',
                            'focus:border-chariot-brand-primary transition-colors focus:outline-hidden',
                            disabled && 'cursor-not-allowed opacity-50'
                          )}
                        />
                        {dayLocalTime && (
                          <span className="text-chariot-text-tertiary text-xs sm:text-sm">
                            ({dayLocalTime})
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle button */}
      <button
        type="button"
        onClick={handleToggleMode}
        disabled={disabled}
        aria-label={
          isPerDayMode
            ? 'Use same time for all days'
            : 'Use different times per day'
        }
        className={cn(
          'text-chariot-brand-primary text-sm font-medium',
          'hover:text-chariot-brand-primary/80 transition-colors',
          'focus:underline focus:outline-hidden',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {isPerDayMode
          ? 'Use same time for all days'
          : 'Use different times per day'}
      </button>
    </div>
  );
}
